<template>
  <!-- Autocomplete maintenance externe, calqué sur EmployeeAutocomplete. La table dépasse
       100 000 lignes : on n'en charge jamais la totalité. Ouvrir le champ propose les
       dernières maintenances (comme l'autocomplete agence propose ses premières agences),
       puis la recherche part au serveur à la frappe. Rendu plafonné à LIMIT dans les deux
       cas. -->
  <v-select
    :value="selectedOption"
    :options="results"
    :filterable="false"
    label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    class="maintenance-ac"
    :class="'maintenance-ac--' + variant"
    @input="onInput"
    @search="onSearch"
  >
    <template slot="option" slot-scope="opt">
      <div class="maintenance-ac__opt">{{ opt.label }}</div>
    </template>

    <template slot="selected-option" slot-scope="opt">
      <span class="maintenance-ac__label">{{ opt.label }}</span>
    </template>

    <template slot="no-options" slot-scope="{ search }">
      <div class="maintenance-ac__empty">
        <template v-if="isTooShort(search)">
          <i class="far fa-keyboard"></i> Tapez au moins {{ minChars }} caractères…
        </template>
        <template v-else-if="normalize(search).trim()">
          Aucune maintenance pour «&nbsp;{{ search }}&nbsp;»
        </template>
        <template v-else>Aucune maintenance externe</template>
      </div>
    </template>

    <li v-if="hiddenCount > 0" slot="list-footer" class="maintenance-ac__more">
      <i class="far fa-ellipsis-h"></i> {{ hiddenCount.toLocaleString("fr-FR") }} autre(s) —
      {{ currentQuery ? "précisez la recherche" : "saisissez pour rechercher" }}
    </li>
  </v-select>
</template>

<script>
// Nombre de maintenances rendues dans le menu, aussi bien pour la liste d'amorçage
// (à l'ouverture du champ) que pour les résultats d'une recherche.
const LIMIT = 20;
// Longueur minimale de recherche avant d'interroger le serveur.
const MIN_CHARS = 2;
// Délai avant d'interroger le serveur.
const DEBOUNCE = 300;

// Minuscule + suppression des accents, pour un matching tolérant.
function normalize(s) {
  return (s == null ? "" : String(s))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // marques diacritiques combinantes
}

// Une maintenance de l'API vers une option du menu. Partagé par la liste d'amorçage et
// par l'index de recherche, pour que les deux affichent exactement la même chose.
function toOption(m) {
  const numero = m.maintenance_number || "";
  return {
    value: m.id,
    label: numero || String(m.id),
    hay: normalize(numero),
  };
}

export default {
  name: "ExternalMaintenanceAutocomplete",
  props: {
    value: { default: null },
    placeholder: { type: String, default: "Numéro de maintenance…" },
    // Non utilisé : le composant charge lui-même son référentiel. Déclaré parce qu'AdminForm
    // passe `options` uniformément à tous les champs 'autocomplete' — sans la prop, la liste
    // atterrirait en attribut DOM sur le v-select.
    options: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    // 'filter' : champ compact d'une ligne de filtres. 'form' : le champ reprend
    // l'habillage global des champs de formulaire.
    variant: { type: String, default: "filter" },
  },
  data() {
    return {
      results: [],
      hiddenCount: 0,
      minChars: MIN_CHARS,
      timer: null,
      // Liste proposée tant que rien n'est saisi, et nombre de maintenances qu'elle laisse
      // de côté (le total renvoyé par l'API).
      defaults: [],
      defaultsHidden: 0,
      // Dernière expression recherchée : distingue « le champ est vide » de « la recherche
      // n'a rien donné », que ce soit pour le message ou pour le pied de liste.
      currentQuery: "",
    };
  },
  computed: {
    maintenances() {
      return this.$store.getters["external_maintenance/getAll"] || [];
    },
    /** Index normalisé, recalculé seulement si la liste du store change. */
    index() {
      return this.maintenances.map(toOption);
    },
    // L'option sélectionnée est cherchée dans l'index complet (pas dans `results`), pour que
    // le libellé s'affiche même hors résultats filtrés. Comparaison souple : une valeur
    // relue d'un formulaire peut arriver sous forme de chaîne.
    selectedOption() {
      if (this.value == null || this.value === "") return null;
      return this.index.find((o) => String(o.value) === String(this.value)) || null;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler: "ensureSelectionLoaded",
    },
  },
  mounted() {
    // Autonome : le composant charge lui-même de quoi remplir le menu à l'ouverture. Une page
    // suffit — le reste ne se trouve que par la recherche.
    this.$store
      .dispatch("external_maintenance/getLastForSelect", { wait: true, limit: LIMIT })
      .then((response) => {
        const data = (response && response.data) || {};
        this.defaults = (data.externalMaintenances || []).map(toOption);
        this.defaultsHidden = Math.max(0, (data.total || 0) - this.defaults.length);
        // Sauf si l'utilisateur a déjà commencé à taper pendant le chargement.
        if (!this.currentQuery) this.showDefaults();
      })
      .catch(() => {});
  },
  methods: {
    normalize,
    /** Vrai pendant qu'une saisie est trop courte pour partir au serveur. */
    isTooShort(search) {
      const q = normalize(search).trim();
      return q.length > 0 && q.length < MIN_CHARS;
    },
    /** Remet le menu sur la liste d'amorçage (ouverture, ou champ vidé). */
    showDefaults() {
      this.results = this.defaults;
      this.hiddenCount = this.defaultsHidden;
    },
    /**
     * En modification, le formulaire fournit l'identifiant avant que la maintenance soit
     * dans le store : sans ce chargement ciblé, le champ afficherait un libellé vide.
     */
    ensureSelectionLoaded(value) {
      if (value == null || value === "" || this.selectedOption) return;
      this.$store.dispatch("external_maintenance/getAllForSelectByIds", {
        ids: [value],
        add: true,
      });
    },
    onInput(option) {
      this.$emit("input", option ? option.value : null);
    },
    onSearch(search, loading) {
      const q = normalize(search).trim();
      this.currentQuery = q;

      // v-select émet aussi `search` à la remise à zéro qui suit une sélection : on y
      // remontre la liste d'amorçage plutôt qu'un menu vide.
      if (!q) {
        clearTimeout(this.timer);
        this.showDefaults();
        return;
      }

      if (q.length < MIN_CHARS) {
        this.results = [];
        this.hiddenCount = 0;
        return;
      }

      // Le serveur alimente le store ; le classement reste local.
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (loading) loading(true);
        this.$store
          .dispatch("external_maintenance/getAllForSelectBySearch", {
            wait: true,
            search: search,
            add: true,
          })
          .then(() => this.computeResults(q))
          .catch(() => this.computeResults(q))
          .then(() => {
            if (loading) loading(false);
          });
      }, DEBOUNCE);
    },
    computeResults(q) {
      const tokens = q.split(/\s+/);
      const scored = [];
      for (const item of this.index) {
        const s = this.scoreItem(item, tokens);
        if (s > 0) scored.push({ item, s });
      }
      scored.sort((a, b) => b.s - a.s || a.item.label.localeCompare(b.item.label));
      this.hiddenCount = Math.max(0, scored.length - LIMIT);
      this.results = scored.slice(0, LIMIT).map((x) => x.item);
    },
    // Tous les mots de la requête doivent matcher ; le score total classe.
    scoreItem(item, tokens) {
      let total = 0;
      for (const t of tokens) {
        const s = this.scoreToken(item, t);
        if (s <= 0) return 0;
        total += s;
      }
      return total;
    },
    // Priorité : numéro exact > préfixe > sous-chaîne (plus tôt = mieux).
    scoreToken(item, t) {
      if (item.hay === t) return 200;
      const idx = item.hay.indexOf(t);
      if (idx === 0) return 120;
      if (idx > 0) return 60 - Math.min(idx, 30);
      return 0;
    },
  },
};
</script>

<style scoped>
/* Racine bornée à la largeur de la colonne, sinon le toggle grandit avec le
   contenu et la valeur déborde du bloc (l'ellipsis ne peut pas se déclencher). */
.maintenance-ac {
  width: 100%;
}
.maintenance-ac >>> .vs__dropdown-toggle {
  background: #fff;
  max-width: 100%;
  align-items: stretch;
}
.maintenance-ac >>> .vs__selected-options {
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
  padding: 0 2px;
}
.maintenance-ac >>> .vs__selected {
  margin: 0 2px;
  max-width: 100%;
  flex-wrap: nowrap;
  overflow: hidden;
}
/* Valeur sélectionnée : forcée sur une seule ligne, tronquée par « … ». */
.maintenance-ac >>> .vs__selected .maintenance-ac__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.maintenance-ac >>> .vs__search {
  margin: 0;
}
.maintenance-ac >>> .vs__actions {
  padding-top: 0;
}
.maintenance-ac >>> .vs__dropdown-menu {
  padding: 4px 0;
}
.maintenance-ac >>> .vs__dropdown-option {
  padding: 6px 12px;
}
.maintenance-ac >>> .vs__dropdown-option--highlight {
  background: #1ab394;
  color: #fff;
}

/* Ligne de filtres : champ compact, à la hauteur des autres filtres. */
.maintenance-ac--filter >>> .vs__dropdown-toggle {
  border-color: #e5e6e7;
  border-radius: 3px;
  min-height: 31px;
  padding: 0; /* défaut asymétrique 0 0 4px → casse le centrage */
}
.maintenance-ac--filter >>> .vs__selected,
.maintenance-ac--filter >>> .vs__search {
  font-size: 13px;
}
.maintenance-ac--filter .maintenance-ac__opt,
.maintenance-ac--filter .maintenance-ac__label {
  font-size: 13px;
}

/* Formulaire : hauteur, bordure et retrait de 20px viennent de l'habillage global des
   .vs__dropdown-toggle. On ne corrige que le padding vertical asymétrique de vue-select,
   qui décentrerait la valeur. */
.maintenance-ac--form >>> .vs__dropdown-toggle {
  padding: 0 0 0 20px;
}
/* Le menu déroulant est hors du champ : il n'hérite pas des 12px du formulaire mais des
   10px du body. */
.maintenance-ac--form .maintenance-ac__opt {
  font-size: 12px;
}

.maintenance-ac__opt {
  white-space: normal;
  color: #34495e;
}
.maintenance-ac >>> .vs__dropdown-option--highlight .maintenance-ac__opt {
  color: #fff;
}
.maintenance-ac__label {
  color: #34495e;
}
.maintenance-ac__empty {
  padding: 6px 4px;
  color: #909499;
  font-size: 13px;
}
.maintenance-ac__more {
  list-style: none;
  padding: 6px 12px;
  font-size: 12px;
  color: #909499;
  border-top: 1px solid #f1f3f5;
  background: #fafbfc;
}
.maintenance-ac__more i {
  margin-right: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/Global/ExternalMaintenanceAutocomplete.vue